import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    List,
    ListItem,
    Slider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { Profile } from "../User/userDTO";
import {
    live4tvLive4tvApiService,
    ScheduledLiveResponse,
} from "../StreamTargetLive4tvApi/live4tvLive4tvApiService";
import { Live4tvApiStreamTarget } from "../StreamTargetLive4tvApi/live4tvApiDTO";

type CropSettings = {
    x: number;
    y: number;
    zoom: number;
};

type Properties = {
    profile: Profile;
    scheduledLive?: ScheduledLiveResponse | null;
    availableScheduledTargets: Array<Live4tvApiStreamTarget>;
    onSaved: (scheduledLive: ScheduledLiveResponse) => void;
    onCancel: () => void;
};

const PORTRAIT_PRESET = { aspect: 9 / 16, width: 1080, height: 1920 };
const LANDSCAPE_PRESET = { aspect: 16 / 9, width: 1920, height: 1080 };

const DEFAULT_CROP: CropSettings = {
    x: 50,
    y: 50,
    zoom: 1,
};

function pad2(value: number): string {
    return value.toString().padStart(2, "0");
}

function toDateInputValue(date: Date): string {
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function toTimeInputValue(date: Date): string {
    return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
    });
}

function getCropRect(imageWidth: number, imageHeight: number, aspect: number, crop: CropSettings) {
    const imageAspect = imageWidth / imageHeight;
    let baseCropWidth = imageWidth;
    let baseCropHeight = imageHeight;

    if (imageAspect > aspect) {
        baseCropWidth = imageHeight * aspect;
        baseCropHeight = imageHeight;
    } else {
        baseCropWidth = imageWidth;
        baseCropHeight = imageWidth / aspect;
    }

    const cropWidth = baseCropWidth / crop.zoom;
    const cropHeight = baseCropHeight / crop.zoom;
    const maxX = Math.max(0, imageWidth - cropWidth);
    const maxY = Math.max(0, imageHeight - cropHeight);

    return {
        x: (crop.x / 100) * maxX,
        y: (crop.y / 100) * maxY,
        width: cropWidth,
        height: cropHeight,
    };
}

async function createCroppedBlob(
    imageSrc: string,
    crop: CropSettings,
    preset: { aspect: number; width: number; height: number },
): Promise<Blob> {
    const image = await loadImage(imageSrc);
    const rect = getCropRect(image.width, image.height, preset.aspect, crop);
    const canvas = document.createElement("canvas");
    canvas.width = preset.width;
    canvas.height = preset.height;
    const context = canvas.getContext("2d");
    if (!context) {
        throw new Error("Could not create image crop context");
    }

    context.drawImage(
        image,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        preset.width,
        preset.height,
    );

    return await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error("Could not export cropped image"));
                return;
            }
            resolve(blob);
        }, "image/jpeg", 0.92);
    });
}

async function createObjectUrl(
    imageSrc: string,
    crop: CropSettings,
    preset: { aspect: number; width: number; height: number },
) {
    const blob = await createCroppedBlob(imageSrc, crop, preset);
    return URL.createObjectURL(blob);
}

function CropEditor(props: {
    label: string;
    imageSrc?: string | null;
    fallbackImage?: string;
    crop: CropSettings;
    preset: { aspect: number; width: number; height: number };
    previewUrl?: string | null;
    onChange: (crop: CropSettings) => void;
}) {
    const { label, imageSrc, fallbackImage, crop, preset, previewUrl, onChange } = props;
    const sourceToRender = previewUrl || fallbackImage;

    return (
        <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" gutterBottom>{label}</Typography>
            <Box
                sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    overflow: "hidden",
                    backgroundColor: "#101820",
                    aspectRatio: `${preset.width} / ${preset.height}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                }}
            >
                {sourceToRender ? (
                    <img
                        src={sourceToRender}
                        alt={label}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                ) : (
                    <Typography variant="body2" color="white">
                        Upload an image to preview this crop
                    </Typography>
                )}
            </Box>

            {imageSrc ? (
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="caption" display="block">Horizontal framing</Typography>
                        <Slider
                            value={crop.x}
                            min={0}
                            max={100}
                            step={1}
                            onChange={(_, value) => onChange({ ...crop, x: value as number })}
                        />
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">Vertical framing</Typography>
                        <Slider
                            value={crop.y}
                            min={0}
                            max={100}
                            step={1}
                            onChange={(_, value) => onChange({ ...crop, y: value as number })}
                        />
                    </Box>
                    <Box>
                        <Typography variant="caption" display="block">Zoom</Typography>
                        <Slider
                            value={crop.zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            onChange={(_, value) => onChange({ ...crop, zoom: value as number })}
                        />
                    </Box>
                </Stack>
            ) : null}
        </Grid>
    );
}

export default function ScheduledLiveEditor(props: Properties) {
    const {
        profile,
        scheduledLive,
        availableScheduledTargets,
        onSaved,
        onCancel,
    } = props;

    const { t } = useTranslation();
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [selectedAccounts, setSelectedAccounts] = useState<Array<string>>([]);
    const [sourceImage, setSourceImage] = useState<string | null>(null);
    const [portraitCrop, setPortraitCrop] = useState<CropSettings>(DEFAULT_CROP);
    const [landscapeCrop, setLandscapeCrop] = useState<CropSettings>(DEFAULT_CROP);
    const [portraitPreviewUrl, setPortraitPreviewUrl] = useState<string | null>(null);
    const [landscapePreviewUrl, setLandscapePreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const openAuthenticationFailures = (
        failures?: Array<{ reason?: string; actionRequired?: { url?: string } }>,
    ) => {
        failures?.forEach((failure) => {
            const authUrl = failure.actionRequired?.url;
            if ((failure.reason === "authentication_required" || failure.reason === "reconnect_required") && authUrl) {
                window.open(authUrl, "_blank", "noopener,noreferrer");
            }
        });
    };

    useEffect(() => {
        const scheduledDate = scheduledLive?.scheduledAt ? new Date(scheduledLive.scheduledAt) : new Date();
        setTitle(scheduledLive?.title || "");
        setDescription(scheduledLive?.description || "");
        setDate(toDateInputValue(scheduledDate));
        setTime(toTimeInputValue(scheduledDate));
        setSelectedAccounts(scheduledLive?.accounts?.map((account) => account.uuid) || []);
        setSourceImage(null);
        setPortraitCrop(DEFAULT_CROP);
        setLandscapeCrop(DEFAULT_CROP);
        setPortraitPreviewUrl(null);
        setLandscapePreviewUrl(null);
        setError(null);
    }, [scheduledLive]);

    useEffect(() => {
        if (!sourceImage) {
            return;
        }

        let active = true;
        let nextPortraitUrl: string | undefined;
        let nextLandscapeUrl: string | undefined;

        const buildPreviews = async () => {
            const [portraitUrl, landscapeUrl] = await Promise.all([
                createObjectUrl(sourceImage, portraitCrop, PORTRAIT_PRESET),
                createObjectUrl(sourceImage, landscapeCrop, LANDSCAPE_PRESET),
            ]);

            if (!active) {
                URL.revokeObjectURL(portraitUrl);
                URL.revokeObjectURL(landscapeUrl);
                return;
            }

            nextPortraitUrl = portraitUrl;
            nextLandscapeUrl = landscapeUrl;
            setPortraitPreviewUrl((previous) => {
                if (previous) {
                    URL.revokeObjectURL(previous);
                }
                return portraitUrl;
            });
            setLandscapePreviewUrl((previous) => {
                if (previous) {
                    URL.revokeObjectURL(previous);
                }
                return landscapeUrl;
            });
        };

        buildPreviews().catch((previewError) => {
            setError(previewError.message);
        });

        return () => {
            active = false;
            if (nextPortraitUrl) {
                URL.revokeObjectURL(nextPortraitUrl);
            }
            if (nextLandscapeUrl) {
                URL.revokeObjectURL(nextLandscapeUrl);
            }
        };
    }, [sourceImage, portraitCrop, landscapeCrop]);

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setSourceImage(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const handleAccountToggle = (uuid: string) => {
        setSelectedAccounts((previous) => {
            if (previous.includes(uuid)) {
                return previous.filter((item) => item !== uuid);
            }
            return [...previous, uuid];
        });
    };

    const handleSave = async () => {
        if (!title.trim()) {
            setError(t("Choose a title for the scheduled live."));
            return;
        }
        if (!date || !time) {
            setError(t("Choose a schedule date and time."));
            return;
        }
        if (selectedAccounts.length === 0) {
            setError(t("Choose at least one social network account."));
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            let portraitUrl = scheduledLive?.cover?.portraitUrl;
            let landscapeUrl = scheduledLive?.cover?.landscapeUrl;

            if (sourceImage) {
                const [portraitBlob, landscapeBlob] = await Promise.all([
                    createCroppedBlob(sourceImage, portraitCrop, PORTRAIT_PRESET),
                    createCroppedBlob(sourceImage, landscapeCrop, LANDSCAPE_PRESET),
                ]);
                const uploadedCover = await live4tvLive4tvApiService.uploadScheduledLiveCovers(
                    profile.token,
                    new File([portraitBlob], `scheduled-live-portrait.jpg`, { type: "image/jpeg" }),
                    new File([landscapeBlob], `scheduled-live-landscape.jpg`, { type: "image/jpeg" }),
                );
                portraitUrl = uploadedCover.portraitUrl || portraitUrl;
                landscapeUrl = uploadedCover.landscapeUrl || landscapeUrl;
            }

            const scheduledAt = new Date(`${date}T${time}:00`);
            const response = await live4tvLive4tvApiService.saveScheduledLive(profile.token, scheduledLive?.uuid || null, {
                title: title.trim(),
                description,
                scheduledAt: scheduledAt.toISOString(),
                accounts: selectedAccounts,
                cover: {
                    portraitUrl,
                    landscapeUrl,
                },
            });

            openAuthenticationFailures(response.failures);

            onSaved(response);
        } catch (saveError: any) {
            const failureMessages = saveError?.response?.data?.failures?.map((failure: any) => failure.message) || [];
            openAuthenticationFailures(saveError?.response?.data?.failures);
            setError(
                failureMessages.length > 0
                    ? failureMessages.join(" ")
                    : saveError?.response?.data?.message || saveError?.message || t("Failed to save the scheduled live."),
            );
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack spacing={3}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        label={t("Title")}
                        fullWidth
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label={t("Description")}
                        fullWidth
                        multiline
                        minRows={3}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        label={t("Date")}
                        type="date"
                        fullWidth
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        label={t("Time")}
                        type="time"
                        fullWidth
                        value={time}
                        onChange={(event) => setTime(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>

            <Box>
                <Typography variant="subtitle2" gutterBottom>{t("Scheduled accounts")}</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t("Choose from your existing destination accounts. Only connected destinations that support scheduling appear here.")}
                </Typography>
                {availableScheduledTargets.length > 0 ? (
                    <List dense>
                        {availableScheduledTargets.map((target) => (
                            <ListItem key={target.uuid} disableGutters>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedAccounts.includes(target.uuid)}
                                            onChange={() => handleAccountToggle(target.uuid)}
                                        />
                                    }
                                    label={`${target.title} (${target.type})`}
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Alert
                        severity="info"
                        action={
                            <Button color="inherit" size="small" component={RouterLink} to="/stream-targets">
                                {t("Manage destinations")}
                            </Button>
                        }
                    >
                        {t("There are no connected destination accounts available for scheduling yet.")}
                    </Alert>
                )}
            </Box>

            <Box>
                <Typography variant="subtitle2" gutterBottom>{t("Cover image")}</Typography>
                <Button variant="outlined" component="label">
                    {t("Upload image")}
                    <input hidden type="file" accept="image/*" onChange={handleFileChange} />
                </Button>
            </Box>

            <Grid container spacing={3}>
                <CropEditor
                    label={t("Portrait crop")}
                    imageSrc={sourceImage}
                    fallbackImage={scheduledLive?.cover?.portraitUrl}
                    crop={portraitCrop}
                    preset={PORTRAIT_PRESET}
                    previewUrl={portraitPreviewUrl}
                    onChange={setPortraitCrop}
                />
                <CropEditor
                    label={t("Landscape crop")}
                    imageSrc={sourceImage}
                    fallbackImage={scheduledLive?.cover?.landscapeUrl}
                    crop={landscapeCrop}
                    preset={LANDSCAPE_PRESET}
                    previewUrl={landscapePreviewUrl}
                    onChange={setLandscapeCrop}
                />
            </Grid>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={onCancel}>{t("Cancel")}</Button>
                <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? t("Saving...") : scheduledLive ? t("Save changes") : t("Create scheduled live")}
                </Button>
            </Box>
        </Stack>
    );
}
